import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../utils/api'
import { 
  PlusIcon, 
  XIcon,
  SaveIcon,
  EmailIcon,
  PhoneIcon,
  BellIcon,
  EyeIcon
} from './Icons'

interface NotificationTemplate {
  id?: number
  name: string
  template_type: 'email' | 'sms' | 'internal'
  subject?: string
  content: string
  variables: string[]
}

interface NotificationTemplateEditorProps {
  isOpen: boolean
  onClose: () => void
  onSave: (template: NotificationTemplate) => void
  editingTemplate?: NotificationTemplate | null
}

const NotificationTemplateEditor: React.FC<NotificationTemplateEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  editingTemplate
}) => {
  const [template, setTemplate] = useState<NotificationTemplate>({
    name: '',
    template_type: 'email',
    subject: '',
    content: '',
    variables: []
  })
  const [showPreview, setShowPreview] = useState(false)
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({
    deal_name: 'Sample Deal',
    customer_name: 'John Doe',
    deal_amount: '$450,000',
    step_name: 'Document Collection',
    customer_email: 'john.doe@example.com',
    customer_phone: '+1 (555) 123-4567',
    current_date: new Date().toLocaleDateString(),
    current_time: new Date().toLocaleTimeString()
  })

  useEffect(() => {
    if (editingTemplate) {
      setTemplate(editingTemplate)
    } else {
      setTemplate({
        name: '',
        template_type: 'email',
        subject: '',
        content: '',
        variables: []
      })
    }
  }, [editingTemplate])

  const templateTypeOptions = [
    { value: 'email', label: 'Email', icon: EmailIcon, color: 'blue' },
    { value: 'sms', label: 'SMS', icon: PhoneIcon, color: 'green' },
    { value: 'internal', label: 'Internal Notification', icon: BellIcon, color: 'purple' }
  ]

  const defaultTemplates = {
    email: {
      subject: 'Update on your {{deal_type}} - {{deal_name}}',
      content: `Hello {{customer_name}},

We wanted to update you on the progress of your mortgage application.

Deal: {{deal_name}}
Amount: {{deal_amount}}
New Status: {{step_name}}

We will continue to keep you informed as your application progresses.

If you have any questions, please don't hesitate to contact us.

Best regards,
Your Mortgage Team`
    },
    sms: {
      content: 'Hi {{customer_name}}, your mortgage application "{{deal_name}}" has been updated to: {{step_name}}. Contact us if you have questions.'
    },
    internal: {
      content: 'Deal "{{deal_name}}" ({{deal_amount}}) has moved to {{step_name}} for {{customer_name}}'
    }
  }

  const availableVariables = [
    { key: 'deal_name', label: 'Deal Name', description: 'Name/title of the deal' },
    { key: 'customer_name', label: 'Customer Name', description: 'Primary customer name' },
    { key: 'deal_amount', label: 'Deal Amount', description: 'Formatted deal amount' },
    { key: 'step_name', label: 'Step Name', description: 'Current workflow step name' },
    { key: 'step_description', label: 'Step Description', description: 'Description of the current step' },
    { key: 'customer_email', label: 'Customer Email', description: 'Primary customer email' },
    { key: 'customer_phone', label: 'Customer Phone', description: 'Primary customer phone' },
    { key: 'deal_type', label: 'Deal Type', description: 'Type of deal (Purchase, Refinance, etc.)' },
    { key: 'broker_name', label: 'Broker Name', description: 'Name of the assigned broker' },
    { key: 'current_date', label: 'Current Date', description: 'Today\'s date' },
    { key: 'current_time', label: 'Current Time', description: 'Current time' }
  ]

  const handleTemplateTypeChange = (newType: 'email' | 'sms' | 'internal') => {
    const defaultTemplate = defaultTemplates[newType]
    setTemplate({
      ...template,
      template_type: newType,
      subject: newType === 'email' ? (defaultTemplate as any).subject || '' : '',
      content: defaultTemplate.content
    })
  }

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('template-content') as HTMLTextAreaElement
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = template.content
      const before = text.substring(0, start)
      const after = text.substring(end, text.length)
      const newContent = before + `{{${variable}}}` + after
      
      setTemplate({ ...template, content: newContent })
      
      // Set cursor position after inserted variable
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4)
      }, 0)
    }
  }

  const generatePreview = () => {
    let preview = template.content
    
    // Replace variables with preview values
    Object.entries(previewVariables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g')
      preview = preview.replace(regex, value)
    })
    
    return preview
  }

  const generateSubjectPreview = () => {
    if (!template.subject) return ''
    
    let preview = template.subject
    
    // Replace variables with preview values
    Object.entries(previewVariables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g')
      preview = preview.replace(regex, value)
    })
    
    return preview
  }

  const handleSave = async () => {
    try {
      const templateToSave = {
        ...template,
        variables: availableVariables.map(v => v.key)
      }
      
      onSave(templateToSave)
      onClose()
    } catch (error) {
      console.error('Error saving template:', error)
    }
  }

  const getTypeIcon = (type: string) => {
    const option = templateTypeOptions.find(opt => opt.value === type)
    return option ? option.icon : BellIcon
  }

  const getTypeColor = (type: string) => {
    const option = templateTypeOptions.find(opt => opt.value === type)
    return option ? option.color : 'gray'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center shadow-md">
              <EmailIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingTemplate ? 'Edit' : 'Create'} Notification Template
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400">Design email, SMS, or notification templates with variables</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Main Content - Two Column */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Column - Editor */}
          <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-6 space-y-5">
            {/* Template Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={template.name}
                onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                placeholder="e.g., Deal Status Update Email"
              />
            </div>

            {/* Template Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Template Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {templateTypeOptions.map((option) => {
                  const IconComponent = option.icon
                  const isSelected = template.template_type === option.value
                  
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleTemplateTypeChange(option.value as any)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-gray-900 bg-blue-50 dark:bg-blue-900/20 shadow-sm'
                          : 'border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-400'
                      }`}
                    >
                      <IconComponent className={`h-5 w-5 mx-auto mb-1 ${
                        isSelected ? 'text-gray-900 dark:text-gray-600' : 'text-gray-400'
                      }`} />
                      <div className={`text-xs font-medium ${
                        isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {option.label}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Subject (Email only) */}
            {template.template_type === 'email' && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Email Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={template.subject || ''}
                  onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                  placeholder="e.g., Update on {{deal_name}}"
                />
              </div>
            )}

            {/* Content */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                  Content <span className="text-red-500">*</span>
                </label>
                {template.template_type === 'sms' && (
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    template.content.length > 160 
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {template.content.length}/160
                  </span>
                )}
              </div>
              <textarea
                id="template-content"
                value={template.content}
                onChange={(e) => setTemplate({ ...template, content: e.target.value })}
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none font-mono text-sm"
                placeholder="Write your message here... Use variables like {{customer_name}}"
              />
            </div>

            {/* Variables */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Insert Variables
              </label>
              <div className="flex flex-wrap gap-2">
                {availableVariables.slice(0, 8).map((variable) => (
                  <button
                    key={variable.key}
                    type="button"
                    onClick={() => insertVariable(variable.key)}
                    className="px-3 py-1.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:shadow-sm transition-all border border-gray-200 dark:border-gray-600"
                    title={variable.description}
                  >
                    {`{{${variable.key}}}`}
                  </button>
                ))}
              </div>
              {availableVariables.length > 8 && (
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                    Show {availableVariables.length - 8} more variables...
                  </summary>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {availableVariables.slice(8).map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => insertVariable(variable.key)}
                        className="px-3 py-1.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:shadow-sm transition-all border border-gray-200 dark:border-gray-600"
                        title={variable.description}
                      >
                        {`{{${variable.key}}}`}
                      </button>
                    ))}
                  </div>
                </details>
              )}
            </div>
          </div>

          {/* Right Column - Live Preview */}
          <div className="w-1/2 bg-gray-50 dark:bg-gray-900/50 overflow-y-auto p-6">
            <div className="sticky top-0">
              <div className="flex items-center space-x-2 mb-4">
                <EyeIcon className="h-5 w-5 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Live Preview</h3>
              </div>

              {/* Preview Card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Email Subject Preview */}
                {template.template_type === 'email' && template.subject && (
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Subject:</div>
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {generateSubjectPreview()}
                    </div>
                  </div>
                )}
                
                {/* Message Body Preview */}
                <div className="p-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 flex items-center space-x-1">
                    <span>
                      {template.template_type === 'email' ? '📧' : 
                       template.template_type === 'sms' ? '💬' : '🔔'}
                    </span>
                    <span>
                      {template.template_type === 'email' ? 'Email Body' : 
                       template.template_type === 'sms' ? 'SMS Message' : 'Notification'}
                    </span>
                  </div>
                  {template.content ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <div className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white leading-relaxed">
                        {generatePreview()}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 dark:text-gray-500 italic">
                      Start typing to see preview...
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Variables Info */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-2">
                  📝 Preview uses sample data
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                  <div>• Customer: {previewVariables.customer_name}</div>
                  <div>• Deal: {previewVariables.deal_name}</div>
                  <div>• Amount: {previewVariables.deal_amount}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-all font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!template.name || !template.content || (template.template_type === 'email' && !template.subject)}
            className="px-6 py-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-semibold flex items-center space-x-2"
          >
            <SaveIcon className="h-4 w-4" />
            <span>Save Template</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default NotificationTemplateEditor